// The exams assigned to a class, for its instructor (RFC-048, byj-1wv.56.15):
// apart from the class's share assignments, which its Assignments tab lists —
// each exam with its version, its window and how far the class has got,
// opened on its own page or its submissions', and withdrawn here, asked
// first. They are assigned from byj, never on this page: with none, the
// command that assigns one, and the steps that make one (byj-1wv.51.12).
import { useCallback, useEffect, useState } from "react";
import { api, type ClassDetail, type ClassExam } from "../api";
import { navigate } from "../router";
import { t, whenMinute } from "../i18n";
import { CommandLine } from "./CommandLine";
import { useConfirm } from "./Confirm";
import { ExamGuideButton } from "./ExamGuide";
import { Link } from "./Nav";
import { RowMenu } from "./RowMenu";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export function ClassExams({
  cls,
  onCount,
  load = api.classExams,
  withdraw = api.withdrawExam,
}: {
  cls: ClassDetail;
  /** how many exams it found, for its tab's count */
  onCount?: (n: number) => void;
  load?: typeof api.classExams;
  withdraw?: typeof api.withdrawExam;
}) {
  const [exams, setExams] = useState<ClassExam[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const read = useCallback(async () => {
    try {
      const got = await load(cls.id);
      setExams(got);
      setError(null);
      onCount?.(got.length);
    } catch (err) {
      setError(errText(err));
    }
  }, [cls.id, load, onCount]);
  useEffect(() => {
    void read();
  }, [read]);

  const withdrawIt = async (x: ClassExam) => {
    if (!(await confirm(t("exams:withdrawExamConfirm", { cls: cls.name }), { action: t("exams:withdrawExam"), danger: true }))) return;
    try {
      await withdraw(x.id);
    } catch (err) {
      setError(errText(err));
    }
    await read();
  };

  const row = (x: ClassExam) => (
    <li key={x.id} className="share-row assignment-row is-live">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        exm
      </span>
      <div className="row-main">
        <Link to={`/e/${encodeURIComponent(x.exam.id)}`} className="row-title">
          {x.exam.title}
        </Link>
        <div className="row-sub">
          {t("exams:examVersion", { v: x.version })}
          {x.opens_at && <> · {t("exams:opensAt", { w: whenMinute(x.opens_at) })}</>}
          {x.closes_at && <> · {t("exams:closesAt", { w: whenMinute(x.closes_at) })}</>}
          {" · "}
          {t("exams:classExamProgress", { started: x.started, handedIn: x.handed_in })}
        </div>
      </div>
      <div className="row-meta">{x.to_grade > 0 && <span className="chip chip-warn">{t("exams:toGrade", { n: x.to_grade })}</span>}</div>
      <RowMenu
        label={t("shares:moreFor", { title: x.exam.title })}
        items={[
          { label: t("exams:submissionsTitle"), onSelect: () => navigate(`/ea/${encodeURIComponent(x.id)}`) },
          { label: t("exams:openExam"), onSelect: () => navigate(`/e/${encodeURIComponent(x.exam.id)}`) },
          { label: t("exams:withdrawExam"), onSelect: () => void withdrawIt(x), danger: true },
        ]}
      />
    </li>
  );

  return (
    <>
      {confirmDialog}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {exams === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : exams.length === 0 ? (
        <div className="empty-card" data-testid="no-class-exams">
          <h3 className="flush">{t("exams:noClassExams")}</h3>
          <p className="muted small">{t("exams:noClassExamsLead")}</p>
          <CommandLine command={`byj exam publish KIT --class ${cls.join_code ?? "CODE"}`} />
          <ExamGuideButton code={cls.join_code} className="small" />
        </div>
      ) : (
        <ul className="rows" data-testid="class-exams">
          {exams.map(row)}
        </ul>
      )}
    </>
  );
}
